import React, { FC, useRef, useState } from 'react';
import { Alert, Button, DialogActions, DialogContent } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Modal } from '../dialog/modal';
import PasswordLogin from '../../pages/login/components/passwordLogin';
import { IPasswordLoginRef } from '../../pages/login/components/passwordLogin/types';
import { ILoginInfo } from '@/services/api/user.types';
import { passwordlogin } from '../../services/api/user';
import { postLog } from '../../services/api/log';
import { userInfoStore } from '../../models';

interface IIdleLoginModalProps {
  open: boolean;
  /** 重新登录成功：token 已刷新、store 已更新，父级负责关闭弹窗并重置闲置计时 */
  onReloginSuccess: () => void;
  /** 用户关闭弹窗（X / 遮罩 / ESC / 退出按钮），父级负责退出登录并跳转登录页 */
  onClose: () => void;
}

/**
 * 闲置重登弹窗：系统 15 分钟无操作后弹出，要求用户重新登入；
 * 关闭弹窗则视为放弃会话，由父级退出到登录页。
 */
const IdleLoginModal: FC<IIdleLoginModalProps> = ({ open, onReloginSuccess, onClose }) => {
  const formRef = useRef<IPasswordLoginRef>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  // 预填登录邮箱（来自 userInfoStore.email，页面刷新后 requestUserInfo 会重新拉取）
  const userName = userInfoStore.email;

  const handleRelogin = async (values: { userName: string; password: string }) => {
    try {
      setErrorMsg('');
      setLoading(true);
      const { Data } = await passwordlogin({
        email: values.userName,
        password: values.password,
      });
      applyLogin(Data);
    } catch (error) {
      setErrorMsg((error as Error).message);
      setLoading(false);
    }
  };

  /** 闲置重登保持在当前组织；组织列表有变化时兜底选第一个 */
  const applyLogin = (data: ILoginInfo) => {
    const currentOrgId = userInfoStore.organizationId;
    const orgId = data.Organizations.some((org) => org.Id === currentOrgId)
      ? currentOrgId
      : data.Organizations[0]?.Id || '';
    if (orgId) {
      userInfoStore.setOrganization(orgId);
    }
    userInfoStore.setToken(data.AccessToken);
    userInfoStore.updateUserInfo(data);
    postLog();
    setLoading(false);
    onReloginSuccess();
  };

  return (
    <Modal
      open={open}
      modalTitle='重新登入'
      onCloseBtnClick={onClose}
      fullWidth
      maxWidth='xs'
    >
      <DialogContent>
        <Alert severity='warning' sx={{ mb: 1 }}>
          系统已长时间未操作，请重新登入；关闭弹窗将退出登录。
        </Alert>
        <PasswordLogin ref={formRef} onSubmit={handleRelogin} defaultValues={{ userName }} />
        {errorMsg && <Alert severity='error'>{errorMsg}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>退出登錄</Button>
        <LoadingButton
          variant='contained'
          loading={loading}
          onClick={() => formRef.current?.submit()}
        >
          登入
        </LoadingButton>
      </DialogActions>
    </Modal>
  );
};

export default IdleLoginModal;
